
import { useState, useRef, useEffect } from "react";
import { Eye, EyeOff, Pencil, Ruler, MoveHorizontal, ArrowUp, ArrowDown, PenLine, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DisplayLayerInfo } from "./types";
import { motion, AnimatePresence } from "framer-motion";

interface LayerRowProps {
  layer: DisplayLayerInfo;
  onToggleVisibility: (layerId: string) => void;
  onRename: (layerId: string, newName: string) => void;
  onBringForward: (layerId: string) => void;
  onSendBackward: (layerId: string) => void;
}

export function LayerRow({
  layer,
  onToggleVisibility,
  onRename,
  onBringForward,
  onSendBackward,
}: LayerRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isMoving, setIsMoving] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Simplified layer type checking using direct property access
  const isGroup = 'objects' in layer;
  const isMeasurementGroup = isGroup && (layer.type === 'ruler' || layer.type === 'angle');
  const isDrawingGroup = isGroup && (layer.type === 'drawing' || layer.type === 'scalpel');
  
  // Show layer controls for all layers (including groups)
  const showLayerControls = true;

  // For groups, we need to extract the base name without count
  const getEditNameFromLayer = () => {
    if (isGroup) {
      // Extract name without the count in parentheses
      const match = layer.name.match(/^(.*?)\s*\(\d+\)$/);
      return match ? match[1] : layer.name;
    }
    return layer.name;
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditName(getEditNameFromLayer());
  };

  const handleRename = () => {
    // Don't allow empty names
    if (!editName.trim()) {
      setIsEditing(false);
      setEditName('');
      return;
    }
    
    onRename(layer.id, editName);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditName('');
    }
  };

  // Animation when user clicks move buttons
  const handleLayerMove = (moveFunction: (layerId: string) => void) => {
    setIsMoving(true);
    moveFunction(layer.id);
    
    // Reset the animation state after a short delay
    setTimeout(() => {
      setIsMoving(false);
    }, 300); // Match this with the animation duration
  };

  // Simplified getTypeIcon function
  const getTypeIcon = () => {
    if (!isGroup) return null;
    
    if (isMeasurementGroup) {
      return layer.type === 'ruler' 
        ? <Ruler className="h-4 w-4 mr-2" /> 
        : <MoveHorizontal className="h-4 w-4 mr-2" />;
    } 
    
    if (isDrawingGroup) {
      return layer.type === 'drawing' 
        ? <PenLine className="h-4 w-4 mr-2" /> 
        : <Scissors className="h-4 w-4 mr-2" />;
    }
    
    return null;
  };

  // Simplified background color determination
  const getLayerBackgroundClass = () => {
    if (isMeasurementGroup) return "bg-secondary/20";
    if (isDrawingGroup) return layer.type === 'drawing' ? "bg-indigo-50" : "bg-blue-50";
    return "";
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 1, y: 0 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: isMoving ? 1.02 : 1,
        backgroundColor: isMoving ? "rgba(236, 239, 244, 0.6)" : "transparent"
      }}
      transition={{ 
        duration: 0.3,
        ease: "easeInOut" 
      }}
      className={cn(
        "flex items-center justify-between gap-2 p-2 rounded-md hover:bg-muted/50 group transition-colors",
        !layer.visible && "opacity-60",
        getLayerBackgroundClass()
      )}
      ref={rowRef}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => onToggleVisibility(layer.id)}
          title={layer.visible ? "Hide layer" : "Show layer"}
        >
          {layer.visible ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4" />
          )}
        </Button>
        
        {isEditing ? (
          <div className="flex-1 min-w-0">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleRename}
              onKeyDown={handleKeyDown}
              className="h-8"
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-center justify-between flex-1 min-w-0">
            <span className="truncate flex items-center">
              {getTypeIcon()}
              {layer.name}
            </span>
            
            <div className="flex items-center gap-1 opacity-100 group-hover:opacity-100 transition-opacity">
              {showLayerControls && (
                <>
                  {onBringForward && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 bg-muted/30 hover:bg-muted/60"
                      onClick={() => handleLayerMove(onBringForward)}
                      title="Bring forward"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  
                  {onSendBackward && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 bg-muted/30 hover:bg-muted/60"
                      onClick={() => handleLayerMove(onSendBackward)}
                      title="Send backward"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleStartEdit}
                    title="Rename layer"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
